import { isEmpty } from 'lodash';

import {
   Practitioner,
   PractitionerReceptionService,
   ReceptionServiceWithPractitioners,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { Money } from '@dreryk/money-common-api';
import { ReceptionServiceDuration } from '@dreryk/egabinet-private-medical-service-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';

import { ReceptionServiceFormBean } from '../../ReceptionServiceForm';
import { receptionServicePriceVisitor } from '../price/receptionServicePrice.utils';
import { DEFAULT_VALUE_ESTIMATED_DURATION } from '../ReceptionServiceEstimatedDurationField';

import {
   ReceptionServicePractitionerValue,
   WithDefaultConfiguration,
   WithIndividualPrice,
   WithIndividualPriceRange,
   WithMultiplePractitioners,
   WithSinglePractitioner,
} from './receptionServicePractitioner.types';

export const receptionServicePractitionerVisitor = (practitioners: Practitioner[]) => <ACCEPT>(visitor: {
   single?: (practitioner: WithSinglePractitioner) => ACCEPT;
   multiple?: (practitioners: WithMultiplePractitioners) => ACCEPT;
}) => {
   if (isEmpty(practitioners)) {
      return assertUnreachable(undefined);
   }

   const value = practitioners.map(
      (practitioner: Practitioner): ReceptionServicePractitionerValue => {
         return {
            id: practitioner.id,
            displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
            specialties: practitioner.specialtyNames,
            estimatedDuration: DEFAULT_VALUE_ESTIMATED_DURATION,
            usingDefaultConfiguration: true,
         };
      }
   );

   if (value.length === 1) {
      return visitor.single?.({
         practitioner: value.at(0)!,
      });
   }

   return visitor.multiple?.({
      practitioners: {
         selector: [],
         value: value,
      },
   });
};

export const receptionServicePractitionerConfigurationVisitor = (practitioner: ReceptionServicePractitionerValue) => <
   ACCEPT
>(visitor: {
   withDefaultConfiguration?: (data: WithDefaultConfiguration) => ACCEPT;
   withIndividualPrice?: (data: WithIndividualPrice) => ACCEPT;
   withIndividualPriceRange?: (data: WithIndividualPriceRange) => ACCEPT;
}) => {
   if (practitioner.usingDefaultConfiguration) {
      return visitor.withDefaultConfiguration?.(practitioner);
   }

   if (practitioner.priceRangeChecked) {
      return visitor.withIndividualPriceRange?.(practitioner);
   }

   return visitor.withIndividualPrice?.(practitioner);
};

export const receptionServicePractitionerPriceVisitor = (practitioner: ReceptionServicePractitionerValue) => <
   ACCEPT
>(visitor: {
   withDefaultConfiguration?: (data: WithDefaultConfiguration) => ACCEPT;
   withPrice?: (data: WithIndividualPrice) => ACCEPT;
   withPriceRange?: (data: WithIndividualPriceRange) => ACCEPT;
}) => {
   if (practitioner.usingDefaultConfiguration) {
      return visitor.withDefaultConfiguration?.(practitioner);
   }

   if (practitioner.priceRangeChecked) {
      return visitor.withPriceRange?.(practitioner);
   }

   return visitor.withPrice?.(practitioner);
};

export const mapPractitionerToReceptionServicePractitioner = (bean: ReceptionServiceFormBean) => (
   practitioner: Practitioner
): ReceptionServicePractitionerValue => {
   const basicData = {
      id: practitioner.id,
      displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
      specialties: practitioner.specialtyNames,
      estimatedDuration: bean.estimatedDuration,
      usingDefaultConfiguration: true,
   } as const;

   return receptionServicePriceVisitor(bean)<ReceptionServicePractitionerValue>({
      asFree: () => ({
         ...basicData,
      }),
      withPrice: ({ price }) => ({
         ...basicData,
         price: price,
      }),
      withPriceRange: ({ priceRange, priceRangeChecked }) => ({
         ...basicData,
         priceRange: priceRange,
         priceRangeChecked: priceRangeChecked,
      }),
   })!;
};

export const updateReceptionPractitionerWithDefaultConfiguration = (bean: ReceptionServiceFormBean) => (
   practitioner: ReceptionServicePractitionerValue
): ReceptionServicePractitionerValue => {
   if (practitioner.usingDefaultConfiguration) {
      const basicData = {
         id: practitioner.id,
         displayName: practitioner.displayName,
         specialties: practitioner.specialties,
         estimatedDuration: bean.estimatedDuration,
         usingDefaultConfiguration: true,
      } as const;

      return receptionServicePriceVisitor(bean)<ReceptionServicePractitionerValue>({
         asFree: () => ({
            ...basicData,
         }),
         withPrice: ({ price, priceRangeChecked }) => ({
            ...basicData,
            priceRangeChecked: priceRangeChecked,
            price: price,
         }),
         withPriceRange: ({ priceRange, priceRangeChecked }) => ({
            ...basicData,
            priceRangeChecked: priceRangeChecked,
            priceRange: priceRange,
         }),
      })!;
   }

   return practitioner;
};

export const getEstimatedDurationValue = (
   usingDefaultConfiguration: boolean,
   receptionServiceEstimatedDuration: ReceptionServiceDuration,
   practitionerEstimatedDuration: ReceptionServiceDuration | undefined
): number => {
   if (usingDefaultConfiguration) {
      return receptionServiceEstimatedDuration.asMinutes();
   }

   if (practitionerEstimatedDuration) {
      return practitionerEstimatedDuration.asMinutes();
   }

   return DEFAULT_VALUE_ESTIMATED_DURATION;
};

export const mapReceptionServicePractitionerPriceToBean = (data: {
   usingDefaultConfiguration: boolean;
   minPrice: Money | undefined;
   maxPrice: Money | undefined;
}): WithDefaultConfiguration | WithIndividualPrice | WithIndividualPriceRange => {
   if (data.usingDefaultConfiguration) {
      return {
         usingDefaultConfiguration: true,
      };
   }

   const minPrice = data.minPrice!.asNumber();
   const maxPrice = data.maxPrice!.asNumber();

   if (minPrice !== maxPrice) {
      return {
         usingDefaultConfiguration: false,
         priceRangeChecked: true,
         individualPriceRange: {
            min: minPrice,
            max: maxPrice,
         },
      };
   }

   return {
      usingDefaultConfiguration: false,
      priceRangeChecked: false,
      individualPrice: minPrice,
   };
};

export const mapReceptionServicePractitionersToBean = (
   data: ReceptionServiceWithPractitioners,
   practitioners: Practitioner[]
): WithSinglePractitioner | WithMultiplePractitioners => {
   if (isEmpty(data.practitioners)) {
      return assertUnreachable(undefined);
   }

   const value = data.practitioners.map(
      ({
         practitioner,
         usingDefaultConfiguration,
         maxPrice,
         minPrice,
         estimatedDuration,
      }: PractitionerReceptionService): ReceptionServicePractitionerValue => {
         return {
            id: practitioner.id,
            displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
            specialties: practitioner.specialtyNames,
            estimatedDuration: getEstimatedDurationValue(
               usingDefaultConfiguration,
               data.estimatedDuration,
               estimatedDuration
            ),
            ...mapReceptionServicePractitionerPriceToBean({
               usingDefaultConfiguration: usingDefaultConfiguration,
               minPrice: minPrice,
               maxPrice: maxPrice,
            }),
         };
      }
   );

   if (practitioners.length === 1) {
      return {
         practitioner: value.at(0)!,
      };
   }

   return {
      practitioners: {
         selector: [],
         value: value,
      },
   };
};
